<script>
import AppIcon from '@site/components/common/AppIcon.vue'
import { consultStore, useConsult } from '@site/composables/useConsult'
import { CONTACT } from '@site/data/contact'

export default {
  name: 'ConsultModal',
  components: { AppIcon },
  data() {
    const { close } = useConsult()
    return {
      close,
      CONTACT,
    }
  },
  computed: {
    isOpen() {
      return consultStore.isOpen
    },
  },
  methods: {
    onKey(e) {
      if (e.key === 'Escape') this.close()
    },
    onOpenChange(isOpen) {
      if (isOpen) document.body.style.overflow = 'hidden'
      else document.body.style.overflow = ''
    },
  },
  watch: {
    isOpen(newVal) {
      this.onOpenChange(newVal)
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKey)
    if (typeof window !== 'undefined' && this.isOpen) {
      document.body.style.overflow = 'hidden'
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey)
    document.body.style.overflow = ''
  },
}
</script>

<template>
  <transition name="modal">
    <div v-if="isOpen" class="consult-modal" @click.self="close" role="dialog" aria-modal="true">
      <div class="consult-modal__panel">
        <button class="consult-modal__close" @click="close" aria-label="关闭">
          <AppIcon name="close" :size="20" />
        </button>

        <div class="consult-modal__heading">
          <span class="consult-modal__eyebrow">咨询航富通</span>
          <h3>开启你的数字化门店经营</h3>
          <p>现在咨询，即可获得门店管理方案与一对一演示</p>
        </div>

        <div class="consult-modal__body">
          <div class="consult-modal__channels">
            <div class="consult-channel">
              <div class="consult-channel__icon"><AppIcon name="phone" :size="22" /></div>
              <div>
                <small>咨询电话</small>
                <a :href="'tel:' + CONTACT.phone1.tel">{{ CONTACT.phone1.name }} {{ CONTACT.phone1.tel }}</a>
              </div>
            </div>

            <div class="consult-channel">
              <div class="consult-channel__icon consult-channel__icon--wechat"><AppIcon name="user" :size="22" /></div>
              <div>
                <small>微信</small>
                <span>扫码关注「{{ CONTACT.wechat }}」</span>
                <span class="consult-channel__qr">[ 微信二维码占位 ]</span>
              </div>
            </div>

            <div class="consult-channel">
              <div class="consult-channel__icon consult-channel__icon--douyin"><AppIcon name="star" :size="22" /></div>
              <div>
                <small>抖音</small>
                <span>搜索「{{ CONTACT.douyin }}」</span>
                <span class="consult-channel__qr">[ 抖音二维码占位 ]</span>
              </div>
            </div>
          </div>

          <div class="consult-modal__note">
            <p><strong>服务时间：</strong>{{ CONTACT.serviceHours }}</p>
            <p><strong>地址：</strong>{{ CONTACT.address }}</p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>


<style lang="scss" scoped>
.consult-modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  // 深色遮罩 + 柔和光斑，让弹窗面板的毛玻璃有可糊的内容
  background:
    radial-gradient(circle at 20% 25%, rgba(168, 85, 247, 0.32), transparent 45%),
    radial-gradient(circle at 80% 75%, rgba(236, 72, 153, 0.28), transparent 42%),
    rgba(11, 18, 32, 0.6);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);

  &__panel {
    position: relative;
    width: 100%;
    max-width: 560px;
    // 内容区为实色白底，保证卡片对齐清晰；不叠加毛玻璃，避免半透明面板与实色卡片错位
    background: $color-bg-white;
    border-radius: $radius-lg;
    padding: 40px;
    box-shadow: 0 20px 60px rgba(11, 18, 32, 0.28), 0 2px 8px rgba(11, 18, 32, 0.12);
    max-height: 90vh;
    overflow-y: auto;
  }

  &__close {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-text-muted;
    transition: background 0.2s;
    &:hover { background: $color-bg-light; color: $color-text; }
  }

  &__heading {
    text-align: center;
    margin-bottom: 28px;

    .consult-modal__eyebrow {
      display: inline-block;
      font-size: $font-sm;
      font-weight: 700;
      letter-spacing: 0.12em;
      color: $color-secondary;
      margin-bottom: 8px;
    }

    h3 { font-size: 26px; margin-bottom: 8px; }
    p { color: $color-text-muted; }
  }

  &__body { display: flex; flex-direction: column; gap: 20px; }

  &__note {
    border-top: 1px solid rgba(11, 18, 32, 0.06);
    padding-top: 16px;
    color: $color-text-muted;
    font-size: 14px;
    line-height: 1.9;
  }
}

.consult-channel {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  background: $color-bg-light;
  border-radius: $radius-md;

  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(10, 110, 92, 0.1);
    color: $color-primary;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--wechat { background: rgba(18, 184, 160, 0.12); color: $color-secondary; }
    &--douyin { background: rgba(245, 166, 35, 0.14); color: $color-accent; }
  }

  small { color: $color-text-muted; display: block; font-size: 13px; margin-bottom: 2px; }
  a { display: block; color: $color-primary; font-weight: 600; font-size: 17px; &:hover { text-decoration: underline; } }
  > div span { display: block; color: $color-text; font-size: 15px; }

  &__qr {
    margin-top: 6px;
    font-size: 13px !important;
    color: $color-text-light !important;
  }
}

.modal-enter-active, .modal-leave-active { transition: opacity 0.3s; }
.modal-enter-active .consult-modal__panel, .modal-leave-active .consult-modal__panel { transition: transform 0.3s, opacity 0.3s; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .consult-modal__panel, .modal-leave-to .consult-modal__panel { transform: translateY(20px) scale(0.98); opacity: 0; }
</style>



// WEBPACK FOOTER //
// src/site/components/layout/ConsultModal.vue